<template>
  <mf-card-container class="card-container">
    <div>
      <span class="title">Modelos de Vale-troca</span>

      <div class="models-container">
        <div
          :class="[...discountClass, disableTypeSelection && selectedModel !== 'ABSOLUTE_DISCOUNT' ? 'segmented-dim segmented-disabled-cursor' : '']"
          @click="!disableTypeSelection && (selectedModel = 'ABSOLUTE_DISCOUNT')"
        >
          <div class="icon-text-row">
            <mf-icon>payments</mf-icon>
            <span class="icon-label">Vale-troca por desconto</span>
          </div>
          <span class="description">
            Permite que seus clientes troquem pontos por descontos em compras. Defina o valor do desconto, e ofereça mais vantagens no seu programa de
            fidelidade.
          </span>
        </div>

        <div
          :class="[...productClass, disableTypeSelection && selectedModel !== 'PRODUCT' ? 'segmented-dim segmented-disabled-cursor' : '']"
          @click="!disableTypeSelection && (selectedModel = 'PRODUCT')"
        >
          <div class="icon-text-row">
            <mf-icon>shopping_basket</mf-icon>
            <span class="icon-label">Vale-troca por produto</span>
          </div>
          <span class="description">
            Permita que seus clientes troquem pontos por produtos exclusivos. Cadastre os itens disponíveis para resgate e aumente o engajamento do seu programa
            de fidelidade.
          </span>
        </div>
      </div>

      <template v-if="this.campaign.type">
        <div class="form-container">
          <div class="form-column">
            <span class="field-label">Limite de vale-troca (opcional)</span>
            <v-text-field v-model="formattedRescueLimit" placeholder="0" outlined class="number-input" :disabled="disableRescueLimit" @keypress="isNumber" />
            <span class="field-hint"> Defina a quantidade de vales que vão ser disponibilizados. </span>

            <div class="section-container">
              <div v-if="selectedModel === 'ABSOLUTE_DISCOUNT'">
                <span class="section-title">Desconto</span>
                <p class="section-description">É necessário definir o valor deste vale-troca.</p>
                <p class="section-description-bottom">Esse é o valor que será utilizado em suas lojas.</p>
              </div>
              <div v-if="selectedModel === 'PRODUCT'">
                <span class="section-title">Produto</span>
                <p class="section-description">É necessário definir o nome deste vale-troca.</p>
                <p class="section-description-bottom">Esse é o nome que será exibido aos seus clientes.</p>
              </div>
              <v-text-field
                v-show="selectedModel === 'ABSOLUTE_DISCOUNT'"
                v-model.lazy="formattedMoney"
                v-money="money"
                label="Valor do desconto"
                prefix="R$"
                outlined
                :maxlength="10"
                placeholder="10"
                class="text-field"
                :disabled="disableTypeSelection"
                :error="!!discountErrors"
                :error-messages="discountErrors"
                @blur="touchMoneyValidation"
                @keydown="isNumber"
              />
              <v-text-field
                v-show="selectedModel === 'PRODUCT'"
                v-model="campaign.product_description"
                outlined
                :maxlength="70"
                counter="70"
                label="Nome do produto"
                placeholder="Ex: Ferro de passar Singer"
                class="text-field"
                :disabled="disableTypeSelection"
                :error="!!productDescriptionErrors"
                :error-messages="productDescriptionErrors"
                @blur="touchProductDescriptionValidation"
              />
            </div>
          </div>

          <div class="form-column">
            <span class="field-label">Limite por CPF/CNPJ (opcional)</span>
            <v-text-field v-model="formattedUserLimit" placeholder="0" outlined class="number-input" :disabled="disableUserLimit" @keypress="isNumber" />
            <span class="field-hint"> Defina quantas vezes cada pessoa pode usar esse vale. </span>

            <div class="section-container">
              <span class="section-title">Custo em pontos</span>
              <p class="section-description-points">Defina quantos pontos o consumidor precisará ter para poder comprar este vale-troca.</p>
              <v-text-field
                v-model="formattedPoints"
                label="Custo em pontos"
                outlined
                placeholder="3000"
                class="points-input"
                :error="!!pointsErrors"
                :error-messages="pointsErrors"
                :disabled="disablePoints"
                @blur="touchPointsValidation"
                @input="handlePointsInput"
                @keypress="isNumber"
              />
            </div>
          </div>

          <div class="form-column image-column">
            <div>
              <span class="section-title-image">Imagem do vale-troca{{ selectedModel === 'PRODUCT' ? '' : ' (opcional)' }}</span>
              <mf-image-cropper
                :mb-size-limit="1"
                :disabled="disableImage"
                :upload-image="modifyImg"
                :error-message="errorMessage"
                :url="voucherImage"
                :width="500"
                :height="500"
                :show-hint="false"
                class="image-cropper"
                :class="{ 'error--text': imageError }"
                @remove-image="deleteFile"
                @snackbar="showSnackbar"
              />
              <div v-if="imageError && selectedModel === 'PRODUCT'" class="error-message">Adicione uma imagem para o vale-troca de produto</div>
              <small class="image-hint">
                Os formatos aceitos são: <span class="highlight">PNG, JPG ou JPEG</span>. A dimensão recomendada é de
                <span class="highlight-bold">500x500 pixels</span>, ou outros tamanhos na mesma proporção. O tamanho máximo permitido é de
                <span class="highlight-bold">até 1 MB</span>.
              </small>
            </div>
          </div>
        </div>
      </template>
    </div>
  </mf-card-container>
</template>
<script>
import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
import { VMoney } from 'v-money'

export default {
  name: 'SegmentToggle',
  directives: { money: VMoney },
  props: {
    campaign: { type: Object, required: true },
    validations: { type: Object, required: true },
  },
  data() {
    return {
      selectedModel: this.campaign.type,
      imageUploaded: false,
      voucherImage: this.truncateFilename(this.campaign.image) || null,
      imageError: false,
      money: {
        decimal: ',',
        thousands: '.',
        precision: 2,
        masked: false,
      },
    }
  },
  computed: {
    disableTypeSelection() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    disableRescueLimit() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    disableUserLimit() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    disablePoints() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    disableImage() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    discountClass() {
      return ['segmented-base', this.selectedModel === 'ABSOLUTE_DISCOUNT' ? 'segmented-active' : 'segmented-inactive']
    },
    productClass() {
      return ['nonsegmented-base', this.selectedModel === 'PRODUCT' ? 'nonsegmented-active' : 'nonsegmented-inactive']
    },

    formattedMoney: {
      get() {
        if (!this.campaign.money) return ''
        if (typeof this.campaign.money === 'number') {
          return this.campaign.money.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        }
        return this.campaign.money
      },
      set(value) {
        if (!value) {
          this.campaign.money = ''
          return
        }
        const numericValue = parseFloat(value.toString().replace(/\./g, '').replace(',', '.'))
        this.campaign.money = isNaN(numericValue) ? '' : numericValue
      },
    },
    formattedPoints: {
      get() {
        if (!this.campaign.points) return ''
        if (typeof this.campaign.points === 'number') {
          return this.campaign.points.toLocaleString('pt-BR')
        }
        return this.campaign.points
      },
      set(value) {
        if (!value) {
          this.campaign.points = ''
          return
        }
        const numericValue = parseInt(value.toString().replace(/\./g, ''))
        this.campaign.points = isNaN(numericValue) ? '' : numericValue
      },
    },
    formattedUserLimit: {
      get() {
        if (!this.campaign.user_limit) return ''
        if (typeof this.campaign.user_limit === 'number') {
          return this.campaign.user_limit.toLocaleString('pt-BR')
        }
        return this.campaign.user_limit
      },
      set(value) {
        if (!value) {
          this.campaign.user_limit = null
          return
        }
        const numericValue = parseInt(value.toString().replace(/\./g, ''))
        if (isNaN(numericValue)) {
          this.campaign.user_limit = null
        } else if (numericValue === 0) {
          this.campaign.user_limit = null
        } else {
          this.campaign.user_limit = numericValue
        }
      },
    },
    formattedRescueLimit: {
      get() {
        if (!this.campaign.rescue_limit) return ''
        if (typeof this.campaign.rescue_limit === 'number') {
          return this.campaign.rescue_limit.toLocaleString('pt-BR')
        }
        return this.campaign.rescue_limit
      },
      set(value) {
        if (!value) {
          this.campaign.rescue_limit = null
          return
        }
        const numericValue = parseInt(value.toString().replace(/\./g, ''))
        if (isNaN(numericValue)) {
          this.campaign.rescue_limit = null
        } else if (numericValue === 0) {
          this.campaign.rescue_limit = null
        } else {
          this.campaign.rescue_limit = numericValue
        }
      },
    },

    errorMessage() {
      return this.bannerImageError ? 'Selecione uma imagem para o destaque.' : ''
    },
    discountErrors() {
      const val = this.validations?.voucher?.money
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Informe o valor do desconto.')
      return errors[0] || ''
    },
    productDescriptionErrors() {
      const val = this.validations?.voucher?.product_description
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Informe o nome do produto.')
      if (!val.minLength) errors.push('O nome do produto está muito curto.')
      if (!val.maxLength) errors.push('O nome do produto está muito longo.')
      if (!val.notOnlySpace) errors.push('Não é permitido salvar apenas espaços.')
      return errors[0] || ''
    },
    pointsErrors() {
      const val = this.validations?.voucher?.points
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Defina o valor do desconto.')
      return errors[0] || ''
    },
  },
  watch: {
    selectedModel(newType, oldType) {
      this.campaign.type = newType
      this.cleanupFieldsBasedOnType(newType)

      if (this.validations && this.validations.voucher && this.validations.voucher.type && this.validations.voucher.type.$touch) {
        this.validations.voucher.type.$touch()
      }
    },
    'campaign.image': {
      handler(newImage) {
        if (newImage) {
          this.voucherImage = this.truncateFilename(newImage)
          this.imageError = false
        } else {
          this.voucherImage = null
        }
      },
      immediate: true,
    },
    voucherImage: {
      handler(newImage) {
        if (newImage) {
          this.imageError = false
        }
      },
    },
  },
  mounted() {
    this.cleanupFieldsBasedOnType(this.campaign.type)
    this.$emit('register-validation', this.validateImage)
  },
  methods: {
    cleanupFieldsBasedOnType(type) {
      if (type === 'PRODUCT') {
        if (this.campaign.hasOwnProperty('money')) {
          this.$delete(this.campaign, 'money')
        }
        if (!this.campaign.product_description) {
          this.$set(this.campaign, 'product_description', '')
        }
      } else if (type === 'ABSOLUTE_DISCOUNT') {
        if (this.campaign.hasOwnProperty('product_description')) {
          this.$delete(this.campaign, 'product_description')
        }
        if (!this.campaign.hasOwnProperty('money')) {
          this.$set(this.campaign, 'money', '')
        } else if (this.campaign.money === 0 || this.campaign.money === null) {
          this.campaign.money = ''
        }
      }
    },
    showSnackbar(snackbar) {
      this.$snackbar(snackbar)
    },
    deleteFile() {
      this.campaign.image = ''
      this.voucherImage = null
    },
    truncateFilename(url) {
      if (!url) return url

      try {
        const urlParts = url.split('/')
        const filenameWithParams = urlParts[urlParts.length - 1]
        const fullFileName = decodeURIComponent(filenameWithParams.split('?')[0])
        const cleanName = fullFileName.length > 37 ? fullFileName.substring(37) : fullFileName
        if (cleanName.length > 16) {
          const truncatedFilename = cleanName.substring(0, 16) + '...'
          const urlWithoutFilename = url.substring(0, url.lastIndexOf('/') + 1)
          return urlWithoutFilename + truncatedFilename
        }

        return url.substring(0, url.lastIndexOf('/') + 1) + cleanName
      } catch (error) {
        return url
      }
    },
    async modifyImg({ base64, filename }) {
      try {
        const {
          data: { singleUploadBase64 },
        } = await this.$apollo.mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: { filename: filename, base64 },
        })
        const { url } = singleUploadBase64
        this.imageUploaded = true
        this.campaign.image = url
        this.voucherImage = this.truncateFilename(url)
        this.imageError = false
      } catch (error) {
        this.showSnackbar({
          message: 'Erro ao salvar a imagem.',
          snackbarColor: 'error',
        })
      }
    },
    touchMoneyValidation() {
      if (this.validations && this.validations.voucher && this.validations.voucher.money && this.validations.voucher.money.$touch) {
        this.validations.voucher.money.$touch()
      }
    },
    touchProductDescriptionValidation() {
      if (this.validations && this.validations.voucher && this.validations.voucher.product_description && this.validations.voucher.product_description.$touch) {
        this.validations.voucher.product_description.$touch()
      }
    },
    touchPointsValidation() {
      if (this.validations && this.validations.voucher && this.validations.voucher.points && this.validations.voucher.points.$touch) {
        this.validations.voucher.points.$touch()
      }
    },
    handlePointsInput(value) {
      if (!value) {
        this.campaign.points = ''
        return
      }
      const numericValue = value.replace(/[^0-9]/g, '')
      if (numericValue) {
        this.campaign.points = parseInt(numericValue)
      } else {
        this.campaign.points = ''
      }
    },
    validateImage() {
      if (this.selectedModel === 'PRODUCT' && (!this.campaign.image || this.campaign.image.trim() === '')) {
        this.imageError = true
        return false
      }
      this.imageError = false
      return true
    },
    isNumber(evt) {
      const key = evt.key
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']

      if (!/^[0-9]$/.test(key) && !allowedKeys.includes(key)) {
        evt.preventDefault()
      }
    },
    formatCurrency(value) {
      if (!value && value !== 0) return 'R$ -'
      const numericValue = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(numericValue)) return 'R$ -'
      return `R$ ${numericValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
  },
}
</script>

<style scoped>
.card-container {
  margin-top: 24px;
}

.title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 20px;
}

.models-container {
  display: flex;
  justify-content: space-between;
}

.description {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
}

.form-container {
  display: flex;
  gap: 32px;
  margin-top: 32px;
}

.form-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.image-column {
  justify-content: flex-end;
  margin-bottom: 28px;
}

.field-label {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 8px;
}

.number-input {
  width: 298px;
}

.field-hint {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 20px;
  margin-top: -20px;
}

.section-container {
  margin-top: 32px;
}

.section-title {
  font-family: 'Lato';
  font-weight: 700;
  font-size: 22px;
  color: #31363b;
}

.section-title-image {
  font-family: 'Lato';
  font-weight: 700;
  font-size: 22px;
  color: #4b525a;
}

.section-description {
  font-family: 'Lato';
  font-weight: 400;
  font-size: 14px;
  color: #6b7580;
  margin-top: 16px;
}

.section-description-bottom {
  font-family: 'Lato';
  font-weight: 400;
  font-size: 14px;
  color: #6b7580;
  margin-top: -16px;
  margin-bottom: 26px;
}

.section-description-points {
  font-family: 'Lato';
  font-weight: 400;
  font-size: 14px;
  color: #6b7580;
  margin-top: 16px;
  max-width: 290px;
}

.text-field {
  margin-top: 16px;
  max-width: 298px;
  min-width: 200px;
}

.points-input {
  margin-top: 26px;
  width: 298px;
}

.image-cropper {
  margin-top: 16px;
}

.image-hint {
  font-family: 'Lato';
  font-weight: 400;
  font-size: 14px;
  color: #6b7580;
  margin-top: 8px;
  display: block;
}

.highlight {
  color: #00b894;
  font-weight: 500;
}

.highlight-bold {
  color: #00b894;
  font-weight: 700;
  font-size: 14px;
}

.vale-troca-container {
  padding: 24px;
  font-family: sans-serif;
}

.modelo-toggle {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.modelo-box {
  flex: 1;
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: border-color 0.2s, background-color 0.2s;
}

.modelo-box.ativo {
  border-color: #0063c6;
  background-color: #e8f1fc;
}

.modelo-box h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.modelo-box p {
  margin-top: 4px;
  font-size: 14px;
  color: #555;
}

.modelo-icon {
  font-size: 30px;
  margin-top: 4px;
}

.campos-limites {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
}

.campos-detalhes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
}

.campo h4 {
  margin: 0 0 8px;
  font-size: 16px;
  color: #333;
}

.campo p {
  font-size: 14px;
  margin-bottom: 12px;
  color: #555;
}

small {
  font-size: 12px;
  color: #555;
  display: block;
  margin-top: 8px;
}

.mf-card-container-margin-top-24 {
  margin-top: 24px;
}

.span-title-modelo-aplicacao {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 20px;
}

.div-flex-space-between {
  display: flex;
  justify-content: space-between;
}

.segmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 14px 0 0 14px;
  cursor: pointer;
}

.segmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.segmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.segmented-disabled-cursor {
  cursor: default !important;
}

.segmented-dim {
  opacity: 0.5 !important;
}

.nonsegmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 0 14px 14px 0;
  cursor: pointer;
}

.nonsegmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.nonsegmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.nonsegmented-no-mobile {
  background-color: #f6f6f8 !important;
}

.nonsegmented-disabled-cursor {
  cursor: default !important;
}

.nonsegmented-dim {
  opacity: 0.5 !important;
}

.div-flex-row-mb-empty {
  display: flex;
  flex-direction: row;
  margin-bottom: 0px;
}

.span-com-segmento-title {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.span-com-segmento-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
}

.div-flex-row {
  display: flex;
  flex-direction: row;
}

.span-sem-segmento-title {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.span-sem-segmento-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
}

.div-flex-row-lock {
  display: flex;
  flex-direction: row;
  margin-top: 12px;
  align-items: center;
}

.span-lock-info {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.div-segmented-block {
  display: flex;
  margin-top: 32px;
  margin-bottom: 12px;
  flex-direction: row;
}

.div-publico-alvo-step3 {
  flex: 1;
}

.span-publico-alvo-title {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
}

.div-auto-activation-step4 {
  flex: 1;
  margin-left: 14px;
}

.div-flex-row-end {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
}

.span-uso-imediato {
  color: #4b525a;
  font-size: 16px;
  font-weight: 400;
  opacity: 1;
}

.span-uso-imediato-disabled {
  opacity: 0.5 !important;
}

.span-uso-imediato-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  margin-top: 16px;
}

.div-nonsegmented-block {
  display: flex;
  flex: 1;
  flex-direction: row;
  margin-top: 32px;
  gap: 32px;
}

.div-left-code,
.div-right-limit {
  flex: 1;
  max-width: 298px;
}

.span-codigo-cupom {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 8px;
}

.div-flex-row-code {
  display: flex;
  flex-direction: row;
}

.v-text-field-maxwidth298 {
  max-width: 298px !important;
  min-width: 200px;
}

.mf-button-generate-code {
  margin-top: 10px !important;
  margin-left: 20px !important;
}

.span-code-info {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 20px;
  margin-top: -20px;
}

.span-code-info-top0 {
  margin-top: 0px !important;
}

.span-limite-title {
  display: block;
  font-size: 16px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 8px;
}

.mf-number-input-width298 {
  width: 298px !important;
}

.span-limit-info {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-bottom: 20px;
  margin-top: -20px;
}

.hr-separator {
  border: none;
  border-top: 1px solid #c3c8cd;
  margin-bottom: 20px;
}

.div-flex-align-top {
  display: flex;
  align-items: flex-start;
}

.div-half-block {
  flex: 1;
  max-width: 50%;
}

.span-dates-title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 34px;
}

.div-flex-row-dates {
  display: flex;
  flex-direction: row;
}

.div-flex-date {
  flex: 1;
}

.span-label-date {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4b525a;
  margin-bottom: 8px;
}

.hr-vertical-dates {
  height: 70%;
  border-left: 1px solid #6b7580;
  margin: 6px 20px;
}

.div-flex-date-end {
  flex: 1;
  margin-right: 70px;
}

.div-validade-variavel {
  flex: 1;
  margin-left: 14px;
  justify-content: space-between;
}

.span-validade-variavel-desc {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #6b7580;
  margin: 8px 0px;
  max-width: 371px;
}

.div-validade-days {
  max-width: 200px;
  margin-top: 16px;
}

@media (max-width: 1115px) {
  .div-flex-row-dates {
    flex-direction: column;
    align-items: flex-start;
  }

  .hr-vertical-dates {
    display: none;
  }

  .div-flex-date,
  .div-flex-date-end {
    width: 50%;
  }

  .div-flex-date-end {
    margin-right: 0;
    margin-top: 16px;
  }
}

@media (max-width: 1160px) {
  .div-nonsegmented-block {
    flex-direction: column;
    align-items: flex-start;
  }

  .div-left-code,
  .div-right-limit {
    width: 100%;
    max-width: 100%;
  }
}

@media (max-width: 700px) {
  .div-flex-align-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .div-half-block,
  .div-validade-variavel {
    width: 100%;
    max-width: 100%;
  }

  .div-flex-row-dates {
    width: 75%;
  }

  .div-flex-date,
  .div-flex-date-end {
    width: 100%;
    margin-right: 0;
    margin-top: 0;
  }

  .hr-vertical-dates {
    display: none;
  }
}

.icon-text-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0px;
}

.icon-label {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.icon-text-row .v-icon {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-message {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Lato';
}
</style>
